/**
 * Per-URL surface gates, evaluated in the HOST PAGE.
 *
 * The rules arrive from the iframe on the `config` message and are resolved
 * here, synchronously, against `window.location`. That placement is the point
 * of the whole design: the parent is the only side with an authoritative
 * location, so an SPA navigation re-decides in the same frame it happens.
 * Asking the iframe would cost a postMessage round trip — async by definition —
 * and paint the wrong surfaces for a frame or two on every denied route.
 *
 * Everything here fails OPEN. A widget that wrongly shows a surface is a
 * cosmetic problem on one page; a widget that wrongly hides itself is a
 * paying customer's site with no widget on it.
 */
import {
  allSurfacesOff,
  allSurfacesOn,
  evaluateWidgetScopeSafe,
} from "@bluehat/shared/widget-scope/matcher";
import type { SurfaceGates, WidgetSurface } from "@bluehat/shared/widget-scope/surfaces";
import type { WidgetScope } from "@bluehat/shared/widget-scope/schema";
import { PHONE_BREAKPOINT_PX } from "@bluehat/shared/widget-design/inline";

let rules: WidgetScope = { rules: [] };
let gates: SurfaceGates = allSurfacesOn();
/** Master off — every surface down regardless of rules. Set in S5. */
let masterOff = false;
/** "Show on phones" (2026-08-26). Defaults ON — absence must mean today's
 *  behavior on every tenant. When the tenant turns it off, phone viewports
 *  get allSurfacesOff through the SAME seam as masterOff. This is the one
 *  deliberate fail-CLOSED here: it closes only what the tenant explicitly
 *  asked to close, and only below the breakpoint. */
let showOnPhones = true;

/** Replace the ruleset (a `config` message landed). Does not re-evaluate. */
export function setScopeRules(next: WidgetScope | undefined): void {
  rules = next ?? { rules: [] };
}

/** Master off switch — overrides the ruleset entirely while set. */
export function setMasterOff(off: boolean): void {
  masterOff = off;
}

export function isMasterOff(): boolean {
  return masterOff;
}

/** Tenant's "Show on phones" setting (config message; absence = true). */
export function setShowOnPhones(show: boolean): void {
  showOnPhones = show;
}

/** The viewport is phone-narrow NOW. Guarded so a non-browser context (tests,
 *  SSR-ish embeds) reads as desktop rather than throwing. */
export function phoneViewportNow(): boolean {
  return (
    typeof window !== "undefined" &&
    window.matchMedia?.(`(max-width: ${PHONE_BREAKPOINT_PX}px)`).matches === true
  );
}

/** True when the tenant hid the widget on phones AND this viewport is one.
 *  `phoneNow` is injectable for tests (bun has no matchMedia). */
export function isPhoneHidden(phoneNow: boolean = phoneViewportNow()): boolean {
  return !showOnPhones && phoneNow;
}

/**
 * Re-resolve for the current URL and return the gates.
 *
 * Cheap enough to call on every route change: the rule list is tenant-authored
 * (single digits, realistically) and matching is a compiled regex per rule.
 */
export function evaluateScopeForCurrentUrl(): SurfaceGates {
  if (masterOff || isPhoneHidden()) {
    gates = allSurfacesOff();
    return gates;
  }
  gates = evaluateWidgetScopeSafe(rules, {
    host: window.location.hostname,
    url: window.location.href,
  });
  return gates;
}

/** The gates from the last evaluation. */
export function currentGates(): SurfaceGates {
  return gates;
}

/** Is this surface allowed on the page we last evaluated? */
export function surfaceOn(surface: WidgetSurface): boolean {
  return gates[surface];
}

/** Reset — tests only. */
export function resetScopeForTests(): void {
  rules = { rules: [] };
  gates = allSurfacesOn();
  masterOff = false;
  showOnPhones = true;
}
